import { Download, FileText, RefreshCw } from "lucide-react";
import { warnIfIbanChecksumFails } from "@/utils/payrollIbanWarning";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  useRegeneratePaymentFilesMutation,
  type IPaymentFile,
} from "@/services/(company)/payrollRunsApi";
import { useDownloadPaymentFile } from "@/services/(company)/paymentFileDownload";

const safeFormatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const formatAmount = (n: number, currency?: string) => {
  const amount = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const sanitizeFilename = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The name to save under if the server's `Content-Disposition` is unreadable.
 *
 * `fileName` FIRST — it is what the bank requires. For UAE WPS that is the
 * MoHRE convention `<establishmentId13><YYYYMMDD><HHMMSS>.SIF`, while
 * `displayName` is the friendly label this card shows on screen
 * (`WPS-AE-<Branch>-<YYYYMM>.SIF`), which MoHRE rejects on the name alone.
 *
 * Only a fallback: the download service prefers the response header, and the
 * API exposes `Content-Disposition` through CORS so it is normally readable.
 * Preferring the label here meant that whenever it was NOT — a proxy stripping
 * the header, an origin without the CORS exposure — the file silently saved
 * under the wrong name with nothing to indicate it.
 */
const buildDownloadName = (file: IPaymentFile) => {
  const ext = file.fileName?.match(/\.[^.]+$/)?.[0] ?? "";
  const base = file.fileName?.trim() || file.displayName || "payment-file";
  const cleaned = sanitizeFilename(base);
  if (ext && !cleaned.toLowerCase().endsWith(ext.toLowerCase())) {
    return `${cleaned}${ext}`;
  }
  return cleaned;
};

interface IProps {
  runId: number;
  files?: IPaymentFile[];
  isLoading?: boolean;
}

export const PaymentFilesCard = ({ runId, files = [], isLoading }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const { download, isDownloading } = useDownloadPaymentFile();
  const [regenerate, { isLoading: isRegenerating }] =
    useRegeneratePaymentFilesMutation();

  const hasFiles = files.length > 0;

  const handleRegenerate = () => {
    if (isRegenerating) return;
    if (hasFiles) {
      const ok = window.confirm(
        t(
          "company:payrolls.regenerate_all_payment_files_for_this_run_existing_files",
        ),
      );
      if (!ok) return;
    }
    toast
      .promise(
        regenerate({ runId })
          .unwrap()
          .then((res) => warnIfIbanChecksumFails(res, t)),
        {
          loading: hasFiles
            ? t("company:payrolls.regenerating_payment_files")
            : t("company:payrolls.generating_payment_files"),
          success: hasFiles
            ? t("company:payrolls.payment_files_regenerated")
            : t("company:payrolls.payment_files_generated"),
          error: (err: any) =>
            err?.data?.message ??
            err?.message ??
            t("company:payrolls.failed_to_generate"),
        },
      )
      .catch(() => {
        /* toast surfaces */
      });
  };

  return (
    <div className="relative rounded-sm border border-borderSecondary bg-white flex flex-col overflow-hidden">
      {/* top accent bar */}
      <span className="absolute inset-x-0 top-0 h-[3px] bg-linear-to-r from-primary/40 via-primary to-primary/40" />

      <div className="flex items-center justify-between gap-2 px-5 pt-5 pb-4 border-b border-borderSecondary">
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-sm flex items-center justify-center bg-primary/10">
            <FileText size={14} className="text-primary" />
          </div>
          <span className="text-sm font-bold text-gray-900">
            {t("company:payrolls.payment_files")}
          </span>
        </div>
        <PermissionButton
          section={PLATFORM_SECTION_ENUM.PAYROLL}
          action="edit"
          mode="hide"
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={handleRegenerate}
          disabled={isRegenerating}
          title={
            hasFiles
              ? t("company:payrolls.regenerate_all_payment_files_for_this_run")
              : t("company:payrolls.generate_payment_files_for_this_run")
          }
        >
          <RefreshCw
            size={14}
            className={isRegenerating ? "animate-spin" : ""}
          />
          {isRegenerating
            ? hasFiles
              ? t("common:states.regenerating")
              : t("common:states.generating")
            : hasFiles
              ? t("company:payrolls.regenerate")
              : t("company:payrolls.generate")}
        </PermissionButton>
      </div>

      <div className="flex flex-col">
        {isLoading && files.length === 0 ? (
          <div className="px-5 py-8 text-xs text-gray-400 text-center">
            {t("company:payrolls.loading_payment_files")}
          </div>
        ) : files.length === 0 ? (
          <div className="px-5 py-8 text-xs text-gray-400 text-center">
            {t("company:payrolls.no_payment_files_generated_yet")}
          </div>
        ) : (
          files.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/70 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="size-9 shrink-0 rounded-sm flex items-center justify-center bg-primary/10">
                  <FileText size={16} className="text-primary" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {f.displayName || f.fileName}
                  </span>
                  {safeFormatDate(f.generatedAt) && (
                    <span className="text-[11px] text-gray-500 truncate">
                      {t("company:employees.generated_on_date", {
                        date: safeFormatDate(f.generatedAt),
                      })}
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 uppercase tracking-wide truncate">
                    {[
                      f.bank?.name,
                      f.employeeCount > 0
                        ? t("company:payrolls.employee_count", {
                            count: f.employeeCount,
                          })
                        : null,
                      Number.isFinite(Number(f.totalAmount))
                        ? formatAmount(Number(f.totalAmount), f.currency)
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="shrink-0 gap-1.5"
                disabled={isDownloading}
                onClick={() =>
                  download(runId, f.id, { fileName: buildDownloadName(f) })
                }
              >
                <Download size={14} />
                {t("common:buttons.download")}
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
import { useTranslation } from "react-i18next";
